/**
 * Cosa mostrare nella striscia di preavviso, e con che parole.
 *
 * 🔑 **Non c'è nessun `Date.now()` in questo file, ed è la sua proprietà
 * principale.** `decidiAvviso` riceve due istanti letti dallo **stesso**
 * orologio — quello del server — e un tempo *trascorso* misurato in locale col
 * cronometro (`performance.now()`, monotono). Sottrae i due istanti fra loro e
 * poi scala il trascorso: l'ora del client non entra mai nel calcolo.
 *
 * Il motivo è concreto: un portatile con l'orologio sbagliato di dieci minuti
 * — che capita, e capita senza che nessuno se ne accorga — mostrerebbe
 * altrimenti un conto alla rovescia già scaduto, o mezz'ora di tempo che non
 * esiste. E in questa applicazione la conseguenza non è cosmetica: è qualcuno
 * che continua a scrivere in un modulo credendo di avere tempo.
 *
 * Sta fuori dal componente anche per una ragione più terrena: il frontend non
 * ha un ambiente di prova per React (`npm test` è `node --test` su moduli
 * `.ts` puri). Dentro il JSX, questa logica non sarebbe provabile affatto.
 */

export type FaseAvviso = 'nascosto' | 'attesa' | 'ultimoMinuto' | 'inCorso';

export interface Avviso {
  fase: FaseAvviso;
  /** Secondi che mancano al fermo. Zero quando è già cominciato. */
  secondiRimasti: number;
}

/**
 * Oltre questo tempo dallo zero, un fermo che non è avvenuto ha smesso di
 * essere una notizia e la striscia sparisce da sola.
 *
 * 🔴 Non è una comodità: è la copertura dei due modi di morire in cui sul
 * server non gira più niente che possa cancellare `fermoPrevistoAlle` — il
 * SIGKILL e il riavvio della macchina. Su tutte le altre vie d'uscita ci pensa
 * l'aggiornatore, e nel giro di un minuto la sentinella. Qui si copre il
 * resto, dalla parte del client, dove non serve che nessuno sia vivo.
 */
const SCADENZA_MS = 10 * 60 * 1000;

/** Sotto questa soglia la striscia cambia tono e chiede di salvare adesso. */
const ULTIMO_MINUTO_MS = 60 * 1000;

export function decidiAvviso({
  fermoPrevistoAlle,
  adesso,
  trascorsiDaLetturaMs,
}: {
  fermoPrevistoAlle: string | null;
  adesso: string;
  trascorsiDaLetturaMs: number;
}): Avviso {
  if (fermoPrevistoAlle === null) return { fase: 'nascosto', secondiRimasti: 0 };

  const fermo = Date.parse(fermoPrevistoAlle);
  const letto = Date.parse(adesso);

  // Un istante che non si sa leggere vale quanto un istante che non c'è: meglio
  // nessuna striscia che una striscia con dentro `NaN`.
  if (Number.isNaN(fermo) || Number.isNaN(letto)) {
    return { fase: 'nascosto', secondiRimasti: 0 };
  }

  const rimastiMs = fermo - letto - trascorsiDaLetturaMs;
  const secondiRimasti = Math.max(0, Math.ceil(rimastiMs / 1000));

  if (rimastiMs < -SCADENZA_MS) return { fase: 'nascosto', secondiRimasti: 0 };
  if (rimastiMs <= 0) return { fase: 'inCorso', secondiRimasti: 0 };
  if (rimastiMs <= ULTIMO_MINUTO_MS) return { fase: 'ultimoMinuto', secondiRimasti };
  return { fase: 'attesa', secondiRimasti };
}

/** «4 min 12 s», «2 min», «42 s». Mai un numero negativo. */
export function formattaRimanente(secondi: number): string {
  const totale = Math.max(0, Math.floor(secondi));
  if (totale < 60) return `${totale} s`;

  const minuti = Math.floor(totale / 60);
  const resto = totale % 60;
  return resto === 0 ? `${minuti} min` : `${minuti} min ${resto} s`;
}

/**
 * Le soglie, in minuti, a cui l'annuncio ai lettori di schermo cambia.
 *
 * 🔑 **Il punto di questa lista è quante volte NON parla.** Il conto alla
 * rovescia visibile cambia ogni secondo; una regione `aria-live` che lo
 * seguisse verrebbe riletta per intero a ogni battito, il che non è un avviso
 * ma una tortura. Ma il rimedio applicato prima — togliere del tutto il numero
 * dall'annuncio — lasciava chi non vede lo schermo davanti alla stessa frase
 * identica per quattordici minuti, **senza mai sapere quanto gli resta**: cioè
 * senza l'unica informazione che serve a decidere se fare in tempo a finire.
 *
 * A grana grossa parla cinque volte in tutto, e ogni volta dice qualcosa di
 * nuovo. Le soglie sono scelte sui tre preavvisi che l'applicazione offre
 * davvero (subito, 5 minuti, 15 minuti): con 15 si sentono 15→10→5→2→1, con 5
 * si sentono 5→2→1.
 */
const SOGLIE_ANNUNCIO_MINUTI = [15, 10, 5, 2, 1] as const;

/**
 * «meno di 5 minuti», a scatti. `null` quando manca più della soglia più alta.
 *
 * Restituisce la soglia **più bassa fra quelle ancora sopra** il tempo che
 * resta, così la frase è vera per costruzione: a 7 minuti dice «meno di 10»,
 * non «circa 7». Un'approssimazione che arrotonda per eccesso non può far
 * credere a nessuno di avere più tempo di quanto ne ha.
 */
export function tempoAGranaGrossa(secondiRimasti: number): string | null {
  const minuti = secondiRimasti / 60;

  let soglia: number | null = null;
  for (const s of SOGLIE_ANNUNCIO_MINUTI) if (minuti <= s) soglia = s;

  if (soglia === null) return null;
  return soglia === 1 ? 'meno di un minuto' : `meno di ${soglia} minuti`;
}

/**
 * Le parole, in tre pezzi per una ragione di accessibilità.
 *
 * `annuncio` è scritto per essere **continuato dal numero**: non finisce con
 * un punto, perché nella striscia visibile in mezzo ci va il conto alla
 * rovescia («l'applicazione si fermerà» + «fra 4 min 12 s» + «.»). Da solo,
 * senza quel seguito, non è una frase — è per questo che esiste
 * `annuncioCompleto`: la stessa notizia chiusa e sensata, per chi non vede lo
 * schermo e quindi non vede nemmeno il conto alla rovescia composto lì in mezzo.
 *
 * `annuncioCompleto` e `invito` sono le uniche cose che finiscono nella
 * regione annunciata ai lettori di schermo. **Contengono il tempo, ma a grana
 * grossa** (vedi `tempoAGranaGrossa`): cambiano una manciata di volte in tutto,
 * non ogni secondo.
 *
 * ⚠️ Da questo dipende una proprietà che non si vede leggendo il componente:
 * finché la stringa non cambia, React non tocca il nodo di testo, e un nodo
 * che non cambia non viene riannunciato. **Se qualcuno infilasse qui il numero
 * esatto, la regione ricomincerebbe a parlare a ogni secondo** — ed è il
 * motivo per cui il conto preciso sta solo nel blocco `aria-hidden`.
 */
export function frasiAvviso(
  fase: FaseAvviso,
  secondiRimasti: number,
): { annuncio: string; annuncioCompleto: string; invito: string } {
  switch (fase) {
    case 'attesa': {
      const grana = tempoAGranaGrossa(secondiRimasti);
      return {
        annuncio: "Aggiornamento in arrivo: l'applicazione si fermerà",
        annuncioCompleto:
          grana === null
            ? "Aggiornamento in arrivo: l'applicazione si fermerà fra più di quindici minuti."
            : `Aggiornamento in arrivo: l'applicazione si fermerà fra ${grana}.`,
        invito: 'Salva quello che stai scrivendo.',
      };
    }
    case 'ultimoMinuto':
      // La fase **è** l'ultimo minuto: la grana grossa qui direbbe sempre la
      // stessa cosa, e scritta a mano si legge meglio.
      return {
        annuncio: "L'applicazione si ferma",
        annuncioCompleto: "L'applicazione si ferma fra meno di un minuto.",
        invito: 'Salva ora.',
      };
    case 'inCorso':
      // Qui non c'è nessun numero da incastrare: `annuncio` è già una frase
      // chiusa, e coincide con `annuncioCompleto`.
      return {
        annuncio: 'Il fermo sta cominciando.',
        annuncioCompleto: 'Il fermo sta cominciando.',
        invito: 'Torna fra qualche minuto.',
      };
    case 'nascosto':
      return { annuncio: '', annuncioCompleto: '', invito: '' };
  }
}
